import React from "react";
import { Link } from "react-router-dom";
import { MapPin, ShieldCheck, Ticket, MessageCircle, Building2, Car, Briefcase, Hammer, Sparkles, Store, Users, BadgeCheck } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";

export default function About() {
  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="about-page">
      <Header />
      <main className="flex-1">
        <section className="bg-primary text-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 md:py-20">
            <p className="text-accent text-sm font-semibold tracking-wider uppercase mb-3">About Us</p>
            <h1 className="font-display text-4xl md:text-5xl font-bold leading-tight">
              The Pataudi — a local marketplace connecting buyers and sellers.
            </h1>
            <p className="text-white/80 mt-4 max-w-2xl text-base md:text-lg">
              Find. Connect. Done. We bring buyers, sellers and professionals in Gurugram, Pataudi and nearby areas together in one clean, trusted place — no middlemen, no clutter, just direct contact.
            </p>
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-4 sm:px-6 py-12 md:py-16">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            <div>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-ink mb-3">Who we are</h2>
              <p className="text-ink/80 leading-relaxed">
                The Pataudi is a listing platform — our role is to connect buyers with sellers of properties, vehicles, businesses and local services. We do not own any listings and we do not verify property ownership independently. Every buyer is advised to verify details before making a decision.
              </p>
              <p className="text-ink/80 leading-relaxed mt-3">
                Sellers post listings for free within their plan quota. Serious buyers can unlock verified contact details using paid credits. Businesses and services are always free to browse and contact.
              </p>
            </div>
            <div>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-ink mb-3">What makes us different</h2>
              <ul className="space-y-3 text-ink/80">
                <li className="flex gap-3"><MapPin className="h-5 w-5 text-accent shrink-0 mt-0.5" /> Hyper-local to Gurugram &amp; Pataudi — not a national clutter.</li>
                <li className="flex gap-3"><ShieldCheck className="h-5 w-5 text-accent shrink-0 mt-0.5" /> Privacy-first: contact details are locked until a buyer is serious.</li>
                <li className="flex gap-3"><BadgeCheck className="h-5 w-5 text-accent shrink-0 mt-0.5" /> Verified Seller badge (₹249, one-time lifetime) for higher trust.</li>
                <li className="flex gap-3"><MessageCircle className="h-5 w-5 text-accent shrink-0 mt-0.5" /> Direct WhatsApp connect — talk to the seller in one tap.</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="bg-muted/40 border-y border-border">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-ink mb-6 text-center">What you can find here</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: Building2, label: "Properties", desc: "Plots, flats, shops, PGs" },
                { icon: Car, label: "Vehicles", desc: "Cars, bikes, commercial" },
                { icon: Briefcase, label: "Businesses", desc: "For sale or partnership" },
                { icon: Hammer, label: "Services", desc: "Contractors, designers, events" },
              ].map(({ icon: Icon, label, desc }) => (
                <div key={label} className="bg-card rounded-md border border-border p-5">
                  <div className="h-10 w-10 rounded-md bg-accent/10 flex items-center justify-center mb-3">
                    <Icon className="h-5 w-5 text-accent" />
                  </div>
                  <p className="font-display font-semibold text-ink">{label}</p>
                  <p className="text-sm text-ink/60 mt-1">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-4 sm:px-6 py-12 md:py-16">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-ink mb-6">How pricing works</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="rounded-xl border border-border bg-card p-5">
              <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center mb-3">
                <Ticket className="h-5 w-5 text-primary" />
              </div>
              <h3 className="font-display font-semibold text-lg text-ink mb-1">Buyer Credits</h3>
              <p className="text-sm text-ink/70">Buy credits (₹199 / ₹249 / ₹399) to unlock 2 / 5 / 10 seller contacts on property &amp; vehicle listings. One credit unlocks one seller — kept forever.</p>
            </div>
            <div className="rounded-xl border border-border bg-card p-5">
              <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center mb-3">
                <Store className="h-5 w-5 text-primary" />
              </div>
              <h3 className="font-display font-semibold text-lg text-ink mb-1">Seller Listing Quota</h3>
              <p className="text-sm text-ink/70">Free plan comes with a starter quota. Top up any time — ₹199 = +10 listings, ₹299 = +25, ₹599 = +40. Purchases stack on top and never expire until used.</p>
            </div>
            <div className="rounded-xl border border-border bg-card p-5" style={{ borderColor: "#D4AF37" }}>
              <div className="h-10 w-10 rounded-md flex items-center justify-center mb-3" style={{ background: "linear-gradient(135deg,#FFD700 0%,#B8860B 100%)" }}>
                <BadgeCheck className="h-5 w-5 text-white" />
              </div>
              <h3 className="font-display font-semibold text-lg text-ink mb-1">Verified Seller Badge</h3>
              <p className="text-sm text-ink/70">Optional one-time upgrade for ₹249. Adds a golden badge to every listing you post — valid for lifetime, non-refundable.</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-4">All payments are processed securely by Razorpay (UPI, cards, net banking, wallets). Once a plan is activated, it cannot be cancelled or refunded except in duplicate-payment or platform-side failure cases — see our <Link to="/refund" className="underline">Refund Policy</Link>.</p>
        </section>

        <section className="bg-primary text-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 md:py-14 text-center">
            <h2 className="font-display text-2xl md:text-3xl font-bold mb-3">Ready to get started?</h2>
            <p className="text-white/80 mb-6 max-w-xl mx-auto">Browse listings or post yours in minutes.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild className="bg-accent hover:bg-accent/90 text-white rounded-sm h-11 px-6" data-testid="about-browse-btn">
                <Link to="/listings">Browse Listings</Link>
              </Button>
              <Button asChild variant="outline" className="rounded-sm h-11 px-6 border-white/30 text-white hover:bg-white/10 bg-transparent" data-testid="about-post-btn">
                <Link to="/dashboard/create-listing">Post a Listing</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
